import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { SiSolidity, SiJavascript, SiReact, SiNodedotjs, SiRust, SiEthereum } from 'react-icons/si';
import { FaCode, FaShieldAlt, FaNetworkWired, FaTools } from 'react-icons/fa';

const SkillsSection = styled.section`
  padding: 5rem 2rem;
  background: rgba(0, 0, 0, 0.2);
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Title = styled(motion.h2)`
  font-size: 2.5rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: 3rem;
  color: #00d4ff;
`;

const SkillsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
`;

const SkillCategory = styled(motion.div)`
  background: rgba(0, 212, 255, 0.05);
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 15px;
  padding: 2rem;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(0, 212, 255, 0.1);
    border-color: rgba(0, 212, 255, 0.4);
    transform: translateY(-5px);
  }
`;

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const CategoryIcon = styled.div`
  font-size: 2rem;
  color: #00d4ff;
`;

const CategoryTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 600;
  color: #ffffff;
`;

const SkillsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem;
`;

const SkillTag = styled(motion.span)`
  background: rgba(0, 212, 255, 0.2);
  color: #00d4ff;
  padding: 0.5rem 1rem;
  border-radius: 25px;
  font-size: 0.9rem;
  font-weight: 500;
  border: 1px solid rgba(0, 212, 255, 0.3);
  transition: all 0.3s ease;

  &:hover {
    background: rgba(0, 212, 255, 0.3);
    transform: scale(1.05);
  }
`;

const TechIconsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin-top: 3rem;
  flex-wrap: wrap;
`;

const TechIcon = styled(motion.div)`
  font-size: 3rem;
  color: #00d4ff;
  padding: 1rem;
  background: rgba(0, 212, 255, 0.1);
  border-radius: 15px;
  border: 1px solid rgba(0, 212, 255, 0.2);
  transition: all 0.3s ease;

  &:hover {
    background: rgba(0, 212, 255, 0.2);
    transform: translateY(-5px);
  }
`;

const Skills = () => {
  const skillCategories = [
    {
      icon: <FaCode />,
      title: "Blockchain Development",
      skills: ["Solidity", "Web3.js", "Ethers.js", "Node.js", "JavaScript", "Rust"]
    },
    {
      icon: <SiEthereum />,
      title: "Token Standards",
      skills: ["ERC-20", "ERC-721", "ERC-1155", "ERC-4907", "BEP-20"]
    },
    {
      icon: <FaShieldAlt />,
      title: "DeFi and Security",
      skills: ["Cryptography", "Security Auditing", "DeFi Protocols", "Smart Contract Security"]
    },
    {
      icon: <FaNetworkWired />,
      title: "Platforms",
      skills: ["Ethereum", "Polygon", "IPFS", "Binance Smart Chain"]
    },
    {
      icon: <FaTools />,
      title: "Tools & Frameworks",
      skills: ["Truffle", "Hardhat", "Geth", "React", "P2P Networking"]
    }
  ];

  const techIcons = [
    <SiSolidity />,
    <SiJavascript />,
    <SiReact />,
    <SiNodedotjs />,
    <SiRust />,
    <SiEthereum />
  ];

  return (
    <SkillsSection id="skills">
      <Container>
        <Title
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          Skills & Technologies
        </Title>

        <SkillsGrid>
          {skillCategories.map((category, index) => (
            <SkillCategory
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.02 }}
            >
              <CategoryHeader>
                <CategoryIcon>{category.icon}</CategoryIcon>
                <CategoryTitle>{category.title}</CategoryTitle>
              </CategoryHeader>
              <SkillsList>
                {category.skills.map((skill, skillIndex) => (
                  <SkillTag
                    key={skillIndex}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {skill}
                  </SkillTag>
                ))}
              </SkillsList>
            </SkillCategory>
          ))}
        </SkillsGrid>

        <TechIconsContainer>
          {techIcons.map((icon, index) => (
            <TechIcon
              key={index}
              initial={{ opacity: 0, scale: 0 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ 
                scale: 1.1,
                rotate: [0, -10, 10, -10, 0],
                transition: { duration: 0.5 }
              }}
            >
              {icon}
            </TechIcon>
          ))}
        </TechIconsContainer>
      </Container>
    </SkillsSection>
  );
};

export default Skills;